/* eslint-disable no-nested-ternary */
// @flow

import deepEqual from 'fast-deep-equal';

/**
 * This module exports a function that can quickly decide if two objects differ
 * and thus whether the feed section should re-render. It is a critical optimization
 * that allows us to not re-render the entire feed every time we infinite scroll.
 *
 * In the future, this should be more tightly coupled with any mutations that can change the factors that matter,
 * and any future mutations should not be possible to introduce without updating this code.   See TRVX-10111
 *
 * See WP-1504 for investigation into a more ideal architecture
 *
 * @author skline
 * @since 2018-10-30
 */

const getFirstItemsObject = section =>
  section.items && section.items.length > 0 ? section.items[0].object : null;

const getFollowing = obj => (obj && obj.userProfile ? obj.userProfile.isFollowing : null);

const getLikes = obj => {
  const referencedMedia =
    obj?.__typename === 'MediaBatch'
      ? (obj?.media || []).map(object => [getFollowing(object), getLikes(object)])
      : null;

  // Don't distinguish between nulls and not existing
  const statistics = Object.entries(obj?.socialStatistics || {}).filter(
    ([, value]) => value !== null,
  );
  const references = obj?.socialReferences?.userReferences || null;
  return [referencedMedia, statistics, references];
};

/**
 *
 */
const factorsGetters = [
  /**
   * Liked or re-posted or boosted:
   */
  section => {
    const obj = getFirstItemsObject(section);
    return getLikes(obj);
  },

  /**
   * Following status on a card:
   */
  section => getFollowing(getFirstItemsObject(section)),

  /**
   * Following original author of the repost's object:
   */
  section => {
    if (section.type !== 'REPOST') {
      return null;
    }

    const obj = getFirstItemsObject(section);
    const references = obj && obj.socialReferences && obj.socialReferences.userReferences;
    return obj && obj.repostedObject
      ? [getFollowing(obj.repostedObject), obj.repostedObject.socialStatistics, references]
      : obj && obj.socialReferences;
  },

  /**
   * Following status on a shelf w/ suggested followees:
   */
  section => {
    if (section.type !== 'PROFILE_LIST') {
      return null;
    }

    return section.items.map(profile => profile.object.isFollowing);
  },

  /**
   * Video, Photos, and LinkPost locations and text.  Schema looks a little different, but does the same thing...
   */
  section => {
    if (section.type !== 'VIDEO' && section.type !== 'LINK_POST' && section.type !== 'PHOTO') {
      return null;
    }

    const obj = getFirstItemsObject(section);
    if (!obj) {
      return null;
    }
    let locations = obj.orderedLocations;
    if (!locations) {
      locations =
        obj.tags != null ? obj.tags.orderedLocations : obj.location ? [obj.location] : null;
    }
    const locIds = locations ? locations.map(location => location.locationId) : null;
    let text = obj.comment;
    if (!text) {
      text = obj.caption;
    }
    const references = obj && obj.socialReferences && obj.socialReferences.userReferences;
    return [locIds, text, references];
  },

  /** Blocks */
  section => {
    if (!section.actor) {
      return null;
    }
    if (!section.actor.blockStatus) {
      return null;
    }
    const { isBlocking, isBlockingYou } = section.actor.blockStatus;
    return [isBlocking, isBlockingYou];
  },
];

const allFactorsEqual = (section1, section2) =>
  factorsGetters.every(getter => deepEqual(getter(section1), getter(section2)));

const shouldSectionUpdate = (oldItem: any, newItem: any) => !allFactorsEqual(oldItem, newItem);

export default shouldSectionUpdate;
